import { Box, Flex, ImageProps, Spinner, Text } from '@chakra-ui/react';
import React from 'react';

import {
  Bank,
  CallCenter,
  CreditFrozen,
  Docs,
  Dollaritas,
  Esign,
  JumpingManNoShadow,
  LeasePeaceMeditatorNoShadow,
  OneTwoThree,
  ResultsCheck,
} from '../../../../assets/Images';
import { OtherLienholder } from '../../../../constants/lienholders';
import {
  DealMediaTypeEnum,
  DealType,
  ImportType,
  PayoffRequestStatus,
} from '../../../../gql/generated/graphql';
import { LeaseExpertOpts } from '../../../../hooks/useLeaseExpertOpts';
import { dateOrdinal, getUTCDate } from '../../../../utils/dates';
import {
  payoffRequestFailureStatuses,
  payoffRequestInProgressStatuses,
} from '../../../../utils/tempInfo';
import { DealStateEnum } from '../../../../utils/types/deal';
import { DealStateMap, DealWithFarthestState, computeEffectiveDealState } from '../../utils';
import { Person } from './PersonToggle';
import {
  AwaitingBankFundingBody,
  AwaitingTitleBody,
  CongratsBody,
  LoanPayoffBody,
  TitleProcessingBody,
  TitleReceivedBody,
} from './postSigningStepper/PostSigningStepLearnMoreBodies';

export type Step = {
  label: string;
  description: React.ReactNode;
  completedDescription?: React.ReactNode;
  pendingDescription?: React.ReactNode;
  icon: ((showFrozenIcon?: boolean) => string) | string;
  isAvatarIcon?: boolean;
  timeEstimate?: string;
};

export type CreditStatus = 'frozen' | 'otherFailure' | 'normal';

export type PayoffStepStatus = 'completed' | 'pending' | 'failed';

export type PayoffSource = 'pi' | 'self-serve';

export const getPayoffStepStatus = ({
  is5StepVariant,
  piCompleted,
  isAfterHoursHeld,
  payoffStatus,
}: {
  is5StepVariant: boolean;
  piCompleted: boolean;
  isAfterHoursHeld: boolean;
  payoffStatus?: PayoffRequestStatus | null;
}): PayoffStepStatus | undefined => {
  if (!is5StepVariant) return undefined;
  if (piCompleted) return 'completed';
  if (isAfterHoursHeld && payoffStatus && payoffRequestInProgressStatuses.includes(payoffStatus))
    return 'pending';
  if (isAfterHoursHeld && (!payoffStatus || payoffRequestFailureStatuses.includes(payoffStatus)))
    return 'failed';
  return 'completed';
};

export const GATHERING_OPTIONS_LABEL = 'GATHERING OPTIONS';
export const YOUR_LEASE_EXPERT_LABEL = 'YOUR LEASE EXPERT';

const formatRepName = (name?: string | null): string => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

const gatheringOptionsStep: Step = {
  label: GATHERING_OPTIONS_LABEL,
  description: "We're reviewing your information and finding the best financing options for you.",
  completedDescription: "We've secured financing options for your lease.",
  pendingDescription: "We'll negotiate with banks to secure the best rate.",
  icon: (showFrozenIcon) => (showFrozenIcon ? CreditFrozen : OneTwoThree),
};

const getYourLeaseExpertStep = ({ isPending, closer, isOpen }: LeaseExpertOpts): Step => {
  if (isPending) {
    return {
      label: YOUR_LEASE_EXPERT_LABEL,
      description: "You'll work one-on-one with a financial specialist to finalize everything.",
      icon: CallCenter,
    };
  }

  if (closer?.id) {
    return {
      label: YOUR_LEASE_EXPERT_LABEL,
      description: (
        <>
          We found your lease expert!
          <br />
          <Text as="span" fontWeight="bold">
            {formatRepName(closer.name)}
          </Text>
        </>
      ),
      icon: (showFrozenIcon) => (showFrozenIcon ? CreditFrozen : closer.photo_url || CallCenter),
      isAvatarIcon: !!closer.photo_url,
    };
  }

  if (isOpen) {
    return {
      label: YOUR_LEASE_EXPERT_LABEL,
      description: "We're matching you with a lease expert now.",
      icon: CallCenter,
    };
  }

  return {
    label: YOUR_LEASE_EXPERT_LABEL,
    description:
      "We're currently outside business hours. You'll be matched with a lease expert as soon as we're back.",
    icon: CallCenter,
  };
};

export const ESIGN_STEP_LABEL = 'E-SIGN & DONE!';

const applicationSubmittedStep: Step = {
  label: 'APPLICATION SUBMITTED',
  description: 'Your lease buyout journey is underway.',
  icon: Docs,
};

const eSignStep: Step = {
  label: ESIGN_STEP_LABEL,
  description: 'Your final step. Sign your documents and the car is yours.',
  icon: Esign,
};

export const VERIFY_PAYOFF_LABEL = 'VERIFY PAYOFF';

const getVerifyPayoffStep = ({
  lienholder,
  payoffStepStatus,
  payoffSource,
}: {
  lienholder?: string | null;
  payoffStepStatus?: PayoffStepStatus;
  payoffSource?: PayoffSource;
}): Step => {
  if (payoffStepStatus === 'failed') {
    return {
      label: VERIFY_PAYOFF_LABEL,
      description: 'We need your payoff amount to continue.',
      icon: Bank,
    };
  }

  if (payoffStepStatus === 'pending') {
    const hasNamedLienholder = !!lienholder && lienholder !== OtherLienholder;
    const description = hasNamedLienholder ? (
      <>
        We're retrieving your payoff from{' '}
        <Text as="span" fontWeight="bold" display="inline">
          {lienholder}
        </Text>
        . This can take up to a few minutes.
      </>
    ) : (
      "We're retrieving your payoff. This can take up to a few minutes."
    );

    return {
      label: VERIFY_PAYOFF_LABEL,
      description: (
        <Flex gap={3}>
          <Box>{description}</Box>
          <Box>
            <Spinner speed="1s" size="sm" color="gray.500" />
          </Box>
        </Flex>
      ),
      icon: Bank,
    };
  }

  if (payoffSource === 'self-serve') {
    return {
      label: VERIFY_PAYOFF_LABEL,
      description: (
        <>
          Payoff amount submitted for{' '}
          <Text as="span" fontWeight="bold">
            {lienholder || 'your lienholder'}
          </Text>
          .
        </>
      ),
      icon: Bank,
    };
  }

  return {
    label: VERIFY_PAYOFF_LABEL,
    description: (
      <>
        We've secured your payoff amount from{' '}
        <Text as="span" fontWeight="bold">
          {lienholder || 'your lienholder'}
        </Text>
        .
      </>
    ),
    icon: Bank,
  };
};

export const getSteps = ({
  lienholder,
  variant = '5step',
  leaseExpertOpts,
  payoffStepStatus,
  payoffSource,
}: {
  lienholder?: string | null;
  variant?: '4step' | '5step';
  leaseExpertOpts: LeaseExpertOpts;
  payoffStepStatus?: PayoffStepStatus;
  payoffSource?: PayoffSource;
}): Step[] => [
  applicationSubmittedStep,
  ...(variant === '5step'
    ? [getVerifyPayoffStep({ lienholder, payoffStepStatus, payoffSource })]
    : []),
  gatheringOptionsStep,
  getYourLeaseExpertStep(leaseExpertOpts),
  eSignStep,
];

export type ArrowDir = 'left' | 'right';

export const getArrowDir = (index: number, total: number): ArrowDir => {
  const offset = total % 2 === 0 ? 1 : 0;
  return (index + offset) % 2 === 0 ? 'right' : 'left';
};

// Completed (past) and current steps share the iceBlue fill; future steps fall
// back to the alternating gray palette. Used by both pre- and post-signing steppers.
export const getStepBgColor = (idx: number, activeStep: number, totalSteps: number): string => {
  if (idx <= activeStep) return 'iceBlue';
  return getArrowDir(idx, totalSteps) === 'right' ? 'lightSilver' : 'readOnlyGray';
};

export const resolveStepDescription = (
  step: Step,
  {
    isFrozenOnGathering,
    isOtherFailureOnGathering,
    isCompleted,
    isCurrent,
    currentDescription,
  }: {
    isFrozenOnGathering: boolean;
    isOtherFailureOnGathering: boolean;
    isCompleted: boolean;
    isCurrent: boolean;
    currentDescription?: React.ReactNode;
  },
): React.ReactNode => {
  if (isFrozenOnGathering) return 'A credit freeze or lock is holding up your application.';
  if (isOtherFailureOnGathering)
    return "We're working on putting together your options. Hang tight!";
  if (isCompleted) return step.completedDescription ?? step.description;
  if (isCurrent) return currentDescription ?? step.description;
  return step.pendingDescription ?? step.description;
};

export const getStepLabelColor = ({
  isCompleted,
  isCurrent,
  isPayoffFailed,
}: {
  isCompleted: boolean;
  isCurrent: boolean;
  isPayoffFailed: boolean;
}): string => {
  if (isCompleted) return 'successGreen';
  if (isPayoffFailed) return 'orange.700';
  if (isCurrent) return 'gray.800';
  return 'gray.500';
};

export const getStepIconOpacity = ({
  isCompleted,
  isCurrent,
}: {
  isCompleted: boolean;
  isCurrent: boolean;
}): number | undefined => (!isCompleted && !isCurrent ? 0.5 : undefined);

export const getStepDescriptionColor = ({
  isCurrent,
  isPayoffFailed,
}: {
  isCurrent: boolean;
  isPayoffFailed: boolean;
}): string => {
  if (isPayoffFailed) return 'orange.600';
  if (isCurrent) return 'gray.600';
  return 'gray.500';
};

export type StepRenderState = {
  isCompleted: boolean;
  isCurrent: boolean;
  isFirst: boolean;
  isLast: boolean;
  dir: ArrowDir;
  isPayoffFailedStep: boolean;
  bgColor: string;
  tailColor: string;
  labelColor: string;
  descriptionColor: string;
  iconOpacity: number | undefined;
  isFrozenOnGathering: boolean;
  isOtherFailureOnGathering: boolean;
  icon: string;
};

export const getStepRenderState = ({
  step,
  index,
  activeStep,
  totalSteps,
  creditStatus,
  payoffStepStatus,
  isMobile,
}: {
  step: Step;
  index: number;
  activeStep: number;
  totalSteps: number;
  creditStatus: CreditStatus;
  payoffStepStatus?: PayoffStepStatus;
  isMobile?: boolean;
}): StepRenderState => {
  const isCompleted = index < activeStep;
  const isCurrent = index === activeStep;
  const isFirst = index === 0;
  const isLast = index === totalSteps - 1;
  const dir = getArrowDir(index, totalSteps);

  const isPayoffFailedStep =
    isCurrent && step.label === VERIFY_PAYOFF_LABEL && payoffStepStatus === 'failed';
  const baseBgColor = getStepBgColor(index, activeStep, totalSteps);
  const bgColor = isPayoffFailedStep ? 'orange.100' : baseBgColor;
  const nextBgColor = getStepBgColor(index + 1, activeStep, totalSteps);
  const tailColor = isMobile || isLast ? bgColor : nextBgColor;
  const labelColor = getStepLabelColor({
    isCompleted,
    isCurrent,
    isPayoffFailed: isPayoffFailedStep,
  });
  const descriptionColor = getStepDescriptionColor({
    isCurrent,
    isPayoffFailed: isPayoffFailedStep,
  });
  const iconOpacity = getStepIconOpacity({ isCompleted, isCurrent });

  const isFrozen = creditStatus === 'frozen';
  const isGatheringStep = step.label === GATHERING_OPTIONS_LABEL;
  const isFrozenOnGathering = isFrozen && isGatheringStep;
  const isOtherFailureOnGathering = isCurrent && creditStatus === 'otherFailure' && isGatheringStep;
  const showFrozenIcon = isFrozenOnGathering;
  const icon = typeof step.icon === 'function' ? step.icon(showFrozenIcon) : step.icon;

  return {
    isCompleted,
    isCurrent,
    isFirst,
    isLast,
    dir,
    isPayoffFailedStep,
    bgColor,
    tailColor,
    labelColor,
    descriptionColor,
    iconOpacity,
    isFrozenOnGathering,
    isOtherFailureOnGathering,
    icon,
  };
};

export const FROZEN_UI_STATES: ReadonlyArray<DealStateEnum> = [
  DealStateEnum.StructuringInProgress,
  DealStateEnum.Structuring,
  DealStateEnum.Closing,
];

export const OTHER_FAILURE_TYPES: ReadonlyArray<ImportType> = [
  ImportType.SoftPullFailure,
  ImportType.NoKbbValuesFailure,
  ImportType.AutoStructureFailure,
  ImportType.AutoStructureTimeout,
];

type HeaderContent = {
  header: string;
  subtitle: string;
  onSubtitleClick?: () => void;
};

type HeaderContentOptions = {
  onSubtitleClick?: () => void;
};

export const getHeaderContent = (
  dealState: DealStateEnum,
  options?: HeaderContentOptions,
): HeaderContent => {
  switch (dealState) {
    case DealStateEnum.SoftClose:
      return { header: 'Welcome to Your Dashboard', subtitle: '' };
    case DealStateEnum.StructuringInProgress:
    case DealStateEnum.Structuring:
      return {
        header: 'Reviewing your application',
        subtitle: "No action needed. We'll notify you when your financing options are ready.",
      };
    case DealStateEnum.Closing:
      return {
        header: 'Your lease expert is on it',
        subtitle: 'No action needed. Your lease expert will reach out soon.',
      };
    case DealStateEnum.Closed:
      return {
        header: 'Preparing your documents',
        subtitle: "We'll notify you when your documents are ready to sign.",
      };
    case DealStateEnum.SentForSignatures:
      return {
        header: 'Ready to sign',
        subtitle: 'Review & Sign',
        onSubtitleClick: options?.onSubtitleClick,
      };
    default:
      return {
        header: 'Reviewing your application',
        subtitle: "No action needed. We'll notify you when your financing options are ready.",
      };
  }
};

export const get4StepActiveStep = (deal: DealWithFarthestState): number => {
  const effective = computeEffectiveDealState(deal);
  if (effective >= 3) return 3;
  if (effective >= 2) return 2;
  return 1;
};

const get5StepActiveStep = (
  deal: DealWithFarthestState,
  payoffStepStatus?: PayoffStepStatus,
): number => {
  if (payoffStepStatus === 'pending' || payoffStepStatus === 'failed') return 1;
  const effective = computeEffectiveDealState(deal);
  if (effective >= 3) return 4;
  if (effective >= 2) return 3;
  return 2;
};

export const getStepperActiveStep = ({
  deal,
  is5StepVariant,
  payoffStepStatus,
}: {
  deal: DealWithFarthestState;
  is5StepVariant: boolean;
  payoffStepStatus?: PayoffStepStatus;
}): number =>
  is5StepVariant ? get5StepActiveStep(deal, payoffStepStatus) : get4StepActiveStep(deal);

// ---------------------------------------------------------------------------
// Post-signing helpers
// ---------------------------------------------------------------------------

export const formatBank = (deal?: DealWithFarthestState): string =>
  deal?.financial_info?.bank || 'your bank';

export const formatLienholder = (deal?: DealWithFarthestState): string => {
  const isRefi = deal?.type === DealType.Refi;
  const fallback = isRefi ? 'your previous lender' : 'your lender';
  const name = deal?.car?.payoff?.lienholder_name;
  if (!name || name === OtherLienholder) return fallback;
  return name;
};

export const formatFirstPaymentDate = (deal?: DealWithFarthestState): string => {
  const raw = deal?.financial_info?.first_payment_date;
  if (!raw) return 'your first payment date';
  return getUTCDate(raw).toLocaleDateString('en-US');
};

// Returns the day the deal first entered its current farthest state, formatted as "May 28th".
// Falls back to empty string when deal_states is missing or empty.
export const formatStartedOnDate = (deal?: DealWithFarthestState): string => {
  const target = deal?.farthestState as DealStateEnum | undefined;
  const states = deal?.deal_states;
  if (!target || !states || !states.length) return '';

  const entries = states.filter((entry) => entry?.state === target);
  if (!entries.length) return '';

  const earliest = entries.reduce((previousEntry, currentEntry) => {
    if (!previousEntry) return currentEntry;
    if (!currentEntry) return previousEntry;
    return getUTCDate(currentEntry.created_at).getTime() <
      getUTCDate(previousEntry.created_at).getTime()
      ? currentEntry
      : previousEntry;
  });
  if (!earliest) return '';

  const date = getUTCDate(earliest.created_at);
  const month = date.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
  const dayOfMonth = date.getUTCDate();
  const day = `${dayOfMonth}${dateOrdinal(dayOfMonth)}`;
  return `${month} ${day}`;
};

export type StepState = 'future' | 'current' | 'past';

export type PostSigningStep = {
  label: string;
  subCopyByState: Record<StepState, React.ReactNode>;
  icon: string;
  iconProps?: ImageProps;
  ModalBody: (props: { deal?: DealWithFarthestState }) => JSX.Element;
};

const getAwaitingBankFundingStep = (deal?: DealWithFarthestState): PostSigningStep => {
  const bank = formatBank(deal);
  return {
    label: 'AWAITING BANK FUNDING',
    subCopyByState: {
      future: `We'll send your documents to ${bank} and wait for them to fund your loan.`,
      current: `Great job signing! Your loan is being funded by ${bank}.`,
      past: `Your loan was funded by ${bank}.`,
    },
    icon: Bank,
    ModalBody: AwaitingBankFundingBody,
  };
};

const getLoanPayoffStep = (deal?: DealWithFarthestState): PostSigningStep => {
  const isRefi = deal?.type === DealType.Refi;
  const lienholder = formatLienholder(deal);
  const lease = isRefi ? 'loan' : 'lease';
  return {
    label: isRefi ? 'PREVIOUS LOAN PAYOFF' : 'LEASE ACCOUNT PAYOFF',
    subCopyByState: {
      future: `Once your loan is funded, we'll use those funds to pay off your ${lease} with ${lienholder}.`,
      current: `We're paying off your ${lease} with ${lienholder}.`,
      past: `Your ${lease} was paid off with ${lienholder}.`,
    },
    icon: Dollaritas,
    ModalBody: LoanPayoffBody,
  };
};

const getAwaitingTitleStep = (deal?: DealWithFarthestState): PostSigningStep => {
  const isRefi = deal?.type === DealType.Refi;
  const lienholder = formatLienholder(deal);
  const lease = isRefi ? 'loan' : 'lease';
  return {
    label: 'AWAITING TITLE',
    subCopyByState: {
      future: `After your ${lease} is paid off, we'll wait for ${lienholder} to send us your vehicle title.`,
      current: `Your ${lease} is closed with ${lienholder}. Waiting on your title.`,
      past: `Your title arrived from ${lienholder}.`,
    },
    icon: LeasePeaceMeditatorNoShadow,
    iconProps: { transform: 'scale(1.15)' },
    ModalBody: AwaitingTitleBody,
  };
};

const getTitleReceivedStep = (): PostSigningStep => ({
  label: 'TITLE RECEIVED',
  subCopyByState: {
    future: "Once your title arrives, we'll prepare your paperwork for state processing.",
    current: "Your title is here. We're preparing your paperwork.",
    past: 'Your paperwork was prepared and sent for processing.',
  },
  icon: ResultsCheck,
  ModalBody: TitleReceivedBody,
});

const getTitleProcessingStep = (): PostSigningStep => ({
  label: 'TITLE PROCESSING',
  subCopyByState: {
    future: "We'll send your title and paperwork to the DMV for final processing.",
    current: 'Your paperwork is with the DMV for final approval.',
    past: 'Your paperwork was approved by the DMV.',
  },
  icon: Docs,
  ModalBody: TitleProcessingBody,
});

const getCongratsStep = (): PostSigningStep => ({
  label: 'CONGRATS!',
  subCopyByState: {
    future:
      "When everything's processed, your title will be updated and the car will officially be yours.",
    current: "Your title's been updated. The car is officially yours.",
    past: '',
  },
  icon: JumpingManNoShadow,
  iconProps: { transform: 'scale(1.28)' },
  ModalBody: CongratsBody,
});

export const getPostSigningSteps = (deal?: DealWithFarthestState): PostSigningStep[] => [
  getAwaitingBankFundingStep(deal),
  getLoanPayoffStep(deal),
  getAwaitingTitleStep(deal),
  getTitleReceivedStep(),
  getTitleProcessingStep(),
  getCongratsStep(),
];

export const POST_SIGNING_TOTAL_STEPS = 6;

export const getPostSigningActiveStep = (deal: DealWithFarthestState): number => {
  const isBooted = deal?.state === DealStateEnum.Booted || !!deal?.request_boot;
  if (isBooted) return 5;

  const farthest = deal?.farthestState as DealStateEnum;
  const value = DealStateMap[farthest];

  if (value >= DealStateMap[DealStateEnum.Finalized]) return 5;
  if (value >= DealStateMap[DealStateEnum.SentToProcessor]) return 4;
  if (value >= DealStateMap[DealStateEnum.TitleReceived]) return 3;
  if (value >= DealStateMap[DealStateEnum.WaitingForTitle]) return 2;
  if (value >= DealStateMap[DealStateEnum.Funded]) return 1;
  return 0;
};

// ---------------------------------------------------------------------------
// Priority documents
// ---------------------------------------------------------------------------

export type PriorityDoc = {
  label: string;
  front: DealMediaTypeEnum;
  back?: DealMediaTypeEnum;
};

const BUYER_PRIORITY_DOCS: PriorityDoc[] = [
  {
    label: "Driver's License",
    front: DealMediaTypeEnum.FrontOfDriversLicense,
    back: DealMediaTypeEnum.BackOfDriversLicense,
  },
  {
    label: 'Insurance Card',
    front: DealMediaTypeEnum.FrontOfInsuranceCard,
  },
];

const COBUYER_PRIORITY_DOCS: PriorityDoc[] = [
  {
    label: "Co-Buyer's License",
    front: DealMediaTypeEnum.FrontOfCoBuyersLicense,
    back: DealMediaTypeEnum.BackOfCoBuyersLicense,
  },
  {
    label: "Co-Buyer's Insurance",
    front: DealMediaTypeEnum.FrontOfCoBuyersInsurance,
  },
];

export const getPriorityDocs = (person: Person): PriorityDoc[] =>
  person === 'cobuyer' ? COBUYER_PRIORITY_DOCS : BUYER_PRIORITY_DOCS;
